'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Typo3Image } from '@/types'
import { BaseLink, Button, Chip, Title, Tooltip } from '@/ui/components'
import { generateAltText } from '@/utils'
import { MEDIA } from '@spot'
import { memo, useMemo } from 'react'
import Img from '../Img/Img'

interface CardVerticalProps extends React.HTMLAttributes<HTMLAnchorElement> {
  title?: string
  subTitle?: string
  image?: Typo3Image
  link?: {
    href?: string
    target?: string
    title?: string
  }
  cardDate?: string
  date?: string
  location?: string
  priceTextInfo?: string
  priceText?: string
  isTopEvent?: boolean
  chips?: string[]
}

const CardVertical = ({
  title,
  subTitle,
  image,
  link,
  className,
  location,
  priceTextInfo,
  priceText,
  chips,
  cardDate,
  isTopEvent,
  ...props
}: CardVerticalProps) => {
  //const D = useStoreI18n.use.dictionary()
  const currentLocale = useStoreI18n.use.currentLocale()

  const imageAlt = useMemo(
    () => generateAltText('story', title, subTitle, currentLocale),
    [title, subTitle, currentLocale]
  )

  return (
    <BaseLink
      {...props}
      href={link?.href || ''}
      fallback={'div'}
      target={link?.target || undefined}
      isTransition={link?.target === '_blank' ? false : true}
      nextLink={link?.target === '_blank' ? false : true}
      className={cn(
        'c-card-vertical group text-primary-100 bg-shop-300 relative block h-full w-full md:flex md:flex-col',
        className
      )}
    >
      <div className="relative">
        <div className="relative aspect-video w-full bg-neutral-200/20">
          {image && (
            <Img
              data={image}
              alt={imageAlt}
              objectCover
              crop={{
                width: 442,
                height: 248
              }}
              fill
              className={'aspect-video'}
              hoverGroupEffect
              sizes={`${MEDIA.tablet} 45rem`}
              quality={90}
            />
          )}
        </div>

        {chips && chips.length > 0 && (
          <div className="gap-2xs absolute bottom-0 left-0 z-10 flex">
            {chips.map((chip, index) => (
              <Chip key={index?.toString()} variant="shop" size="s">
                {chip}
              </Chip>
            ))}
          </div>
        )}
      </div>

      <div className={cn('px-md pb-lg pt-sm')}>
        <div className="gap-y-2xs flex flex-col text-left">
          <Title className={'h4 line-clamp-3'}>{title}</Title>
          {location && (
            <p className="textstyle-md-regular line-clamp-3">{location}</p>
          )}
        </div>
        <div className="mt-xs flex justify-end">
          {priceTextInfo ? (
            <Tooltip>
              <Tooltip.Trigger>
                <Button
                  variant="text"
                  icon="material-symbols:info"
                  iconPosition="right"
                  className="textstyle-md-bold text-primary-100"
                >
                  {priceText}
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>{priceTextInfo}</Tooltip.Content>
            </Tooltip>
          ) : (
            <div className="textstyle-md-bold text-primary-100">
              {priceText}
            </div>
          )}
        </div>
      </div>
    </BaseLink>
  )
}

CardVertical.displayName = 'CardVertical'

export default memo(CardVertical)
